import type { RegistrationTrackingSettings } from '@shared/config';
import type { FormData } from '../formConfig';
import { hasRegistrationAuthReturnParam } from '../hooks/registrationAuthResume';

const REGISTRATION_SUCCESS_TRACKING_KEY = 'registration-success-tracking';

const TRACKING_PERIODS: Record<number, string> = {
  0: 'Monthly',
  1: 'Weekly',
  2: 'Daily',
};

/**
 * Returns the deposit limit period tracking value, or undefined when no limit was set ("No Limits" stores a negative amount).
 */
function getDepositLimitTrackingPeriod(depositLimits: FormData['depositLimits'] | undefined): string | undefined {
  const amount = Number(depositLimits?.amount);
  if (!depositLimits || Number.isNaN(amount) || amount < 0) {
    return undefined;
  }

  return TRACKING_PERIODS[depositLimits.period?.value];
}

function createMarketingTrackingAttributes(formData: FormData): Record<string, string> {
  const trackingPeriod = getDepositLimitTrackingPeriod(formData.depositLimits);

  return {
    'data-track-marketing': formData.createAccount?.privacyPolicy === true ? 'Yes' : 'No',
    ...(trackingPeriod && { 'data-track-period': trackingPeriod }),
  };
}

function createPreferencesTrackingAttributes(formData: FormData, bundleCode: string | null): Record<string, string> {
  const productPreferences = Array.isArray(formData.productPreferences) ? (formData.productPreferences as string[]) : [];
  const channelPreferences = Array.isArray(formData.channelPreferences) ? (formData.channelPreferences as string[]) : [];

  const trackingAttributes: Record<string, string> = {
    'data-track-communication-email': channelPreferences.includes('email') ? 'Yes' : 'No',
    'data-track-communication-sms': channelPreferences.includes('sms') ? 'Yes' : 'No',
    'data-track-communication-mobile': channelPreferences.includes('phone') ? 'Yes' : 'No',
    'data-track-product-lottery': productPreferences.includes('lottery') ? 'Yes' : 'No',
    'data-track-product-casino': productPreferences.includes('casino') ? 'Yes' : 'No',
    'data-track-product-sportsbook': productPreferences.includes('sportsbook') ? 'Yes' : 'No',
  };

  if (bundleCode) {
    trackingAttributes['data-track-offer'] = bundleCode;
  }

  return trackingAttributes;
}

/**
 * Builds the registration success screen tracking attributes for the market's success attribute set.
 */
function createRegistrationSuccessTracking(
  formData: FormData,
  successAttributes: RegistrationTrackingSettings['successAttributes'],
  bundleCode: string | null
): Record<string, string> {
  return successAttributes === 'marketing' ? createMarketingTrackingAttributes(formData) : createPreferencesTrackingAttributes(formData, bundleCode);
}

function saveRegistrationSuccessTracking(attributes: Record<string, string>): void {
  try {
    sessionStorage.setItem(REGISTRATION_SUCCESS_TRACKING_KEY, JSON.stringify(attributes));
  } catch {
    // Storage can be unavailable (private mode, quota); the success screen is then rendered without tracking attributes.
  }
}

function getRegistrationSuccessTracking(): Record<string, string> | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedAttributes = sessionStorage.getItem(REGISTRATION_SUCCESS_TRACKING_KEY);
    return storedAttributes ? (JSON.parse(storedAttributes) as Record<string, string>) : null;
  } catch {
    return null;
  }
}

/**
 * Returns the success screen tracking attributes: the stored ones after the login round-trip (the form is empty by
 * then, so building from it would report every value as "No"), otherwise the ones built from the form.
 */
function resolveRegistrationSuccessTracking(
  formData: FormData,
  successAttributes: RegistrationTrackingSettings['successAttributes'],
  searchParams: Pick<URLSearchParams, 'get'>
): Record<string, string> {
  if (hasRegistrationAuthReturnParam(searchParams)) {
    return getRegistrationSuccessTracking() ?? {};
  }

  return createRegistrationSuccessTracking(formData, successAttributes, searchParams.get('code'));
}

export { createRegistrationSuccessTracking, resolveRegistrationSuccessTracking, saveRegistrationSuccessTracking };
